import { AlertCircleIcon } from "lucide-react";
import { useState } from "react";

import {
  createPreview,
  type FileMetadata,
  type FileWithPreview,
  generateUniqueId,
  isImage,
  useFileUpload,
} from "@krea/shared/hooks/useFileUpload";

import { CropModal } from "./CropModal";
import {
  GridFilePreview,
  ListFilePreview,
  type PreviewStyle,
} from "./FilePreview";

const previewMap = {
  grid: GridFilePreview,
  list: ListFilePreview,
} as const;

// CropModal component using AppModal
interface FileInputProps {
  onFilesChange: (files: FileWithPreview[]) => void; // Make sure this is wrapped in useCallback to prevent re-renders.
  files: (File | FileMetadata)[];
  maxSizeMB?: number;
  accept?: string;
  multiple?: boolean;
  previewStyle?: PreviewStyle;
  enableCropping?: boolean;
  className?: string;
  buttonLabel?: string;
}

export const FileInput = ({
  maxSizeMB = Number.POSITIVE_INFINITY,
  files,
  accept = "*", //  Examples: *, image/*, .pdf
  multiple = true,
  onFilesChange,
  previewStyle = "list",
  enableCropping = false,
  className,
  buttonLabel,
}: FileInputProps) => {
  const maxSize = maxSizeMB ? maxSizeMB * 1024 * 1024 : undefined;
  const [cropModalFile, setCropModalFile] = useState<FileWithPreview | null>(
    null,
  );
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const previewFiles = files.map((file) => ({
    file,
    id: generateUniqueId(file),
    preview: createPreview(file),
  }));

  const onNewFilesAdded = (files: FileWithPreview[], isEditing = false) => {
    if (files.length > 1) return;
    const firstFile = files[0];
    if (!firstFile || !isImage(firstFile)) return;

    if (!isEditing) {
      setCropModalFile(firstFile);
      setIsCropModalOpen(true);
    }
  };

  const handleCropModalOpen = (isOpen: boolean) => {
    setIsCropModalOpen(isOpen);

    if (!isOpen) {
      setCropModalFile(null);
    }
  };

  const [{ errors }, actions] = useFileUpload({
    accept,
    maxSize,
    files: previewFiles,
    multiple,
    onFilesChange,
    onFilesAdded: onNewFilesAdded,
  });

  const PreviewComponent = previewMap[previewStyle];
  const { addFiles, removeFile } = actions;

  return (
    <div className="flex flex-col gap-2">
      <PreviewComponent
        actions={actions}
        files={previewFiles}
        accept={accept}
        multiple={multiple}
        className={className}
        buttonLabel={buttonLabel}
      />

      {errors.length > 0 && (
        <div className="flex items-center gap-1 text-xs text-red" role="alert">
          <AlertCircleIcon className="size-3 shrink-0" />
          <span>{errors[0]}</span>
        </div>
      )}

      {/* Crop Modal */}
      {cropModalFile && enableCropping && (
        <CropModal
          file={cropModalFile}
          isOpen={isCropModalOpen}
          addFile={(file) => addFiles([file])}
          removeFile={(id) => removeFile(id)}
          setIsOpen={handleCropModalOpen}
        />
      )}
    </div>
  );
};
